import { VERSION } from '../version';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

// Debug: Log the API URL being used
console.log('API Service initialized with URL:', API_URL);
console.log('Environment VITE_API_URL:', import.meta.env.VITE_API_URL);

/** Detect OS name + version from navigator.userAgent for X-App-OS-Version. */
function detectOSVersion(): string {
  const ua = navigator.userAgent;
  const mac = ua.match(/Mac OS X ([\d_]+)/);
  if (mac) return `macOS ${mac[1].replace(/_/g, '.')}`;
  const win = ua.match(/Windows NT ([\d.]+)/);
  if (win) return `Windows ${win[1]}`;
  const android = ua.match(/Android ([\d.]+)/);
  if (android) return `Android ${android[1]}`;
  const ios = ua.match(/OS ([\d_]+) like Mac/);
  if (ios) return `iOS ${ios[1].replace(/_/g, '.')}`;
  const linux = ua.match(/Linux/);
  if (linux) return 'Linux';
  return 'unknown';
}

/** Detect browser name + major version for X-App-Device. */
function detectBrowser(): string {
  const ua = navigator.userAgent;
  const chrome = ua.match(/Chrome\/([\d]+)/);
  const safari = ua.match(/Version\/([\d]+).*Safari/);
  const firefox = ua.match(/Firefox\/([\d]+)/);
  const edge = ua.match(/Edg\/([\d]+)/);
  if (edge) return `Edge ${edge[1]}`;
  if (firefox) return `Firefox ${firefox[1]}`;
  if (safari) return `Safari ${safari[1]}`;
  if (chrome) return `Chrome ${chrome[1]}`;
  return 'Browser';
}

/** Static client identification headers — computed once at module load. */
export const CLIENT_HEADERS: Record<string, string> = {
  'X-App-Version':    VERSION,
  'X-App-Platform':   'web',
  'X-App-OS-Version': detectOSVersion(),
  'X-App-Device':     detectBrowser(),
};

// Deduplicate concurrent refresh attempts — all callers wait on the same promise
let refreshPromise: Promise<string | null> | null = null;

async function attemptTokenRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      return data.access_token as string;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
}

class ApiService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...CLIENT_HEADERS,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge with any additional headers from options
    if (options.headers) {
      const optHeaders = options.headers as Record<string, string>;
      Object.assign(headers, optHeaders);
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 — try silent refresh before redirecting to login
    if (response.status === 401) {
      const newToken = await attemptTokenRefresh();
      if (newToken) {
        // Retry the original request with the refreshed token
        const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...options,
          headers: retryHeaders,
        });
        if (retryResponse.ok) {
          if (retryResponse.status === 204) return undefined as T;
          return retryResponse.json() as Promise<T>;
        }
        if (retryResponse.status !== 401) {
          const err = await retryResponse.json().catch(() => ({ detail: 'Request failed' }));
          throw new Error(err.detail || 'Request failed');
        }
      }
      // Refresh failed or retry still 401 — session truly expired
      forceLogout();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    // 204 No Content — no body to parse
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Watchlist API
  async getWatchlists() {
    return this.request<Watchlist[]>('/api/v1/watchlists');
  }

  async createWatchlist(name: string) {
    return this.request<Watchlist>('/api/v1/watchlists', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateWatchlist(id: number, name: string) {
    return this.request<Watchlist>(`/api/v1/watchlists/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name }),
    });
  }

  async deleteWatchlist(id: number) {
    return this.request<void>(`/api/v1/watchlists/${id}`, {
      method: 'DELETE',
    });
  }

  async addWatchlistItem(watchlistId: number, symbol: string, name: string, exchange: string) {
    return this.request<WatchlistItem>(`/api/v1/watchlists/${watchlistId}/items`, {
      method: 'POST',
      body: JSON.stringify({ symbol, name, exchange }),
    });
  }

  async removeWatchlistItem(watchlistId: number, itemId: number) {
    return this.request<void>(`/api/v1/watchlists/${watchlistId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // Portfolio API
  async getPortfolios() {
    return this.request<Portfolio[]>('/api/v1/portfolios');
  }

  async createPortfolio(name: string, description?: string) {
    return this.request<Portfolio>('/api/v1/portfolios', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
    });
  }

  async deletePortfolio(id: number) {
    return this.request<void>(`/api/v1/portfolios/${id}`, {
      method: 'DELETE',
    });
  }

  async addHolding(portfolioId: number, holding: CreateHolding) {
    return this.request<Holding>(`/api/v1/portfolios/${portfolioId}/holdings`, {
      method: 'POST',
      body: JSON.stringify(holding),
    });
  }

  async updateHolding(portfolioId: number, holdingId: number, holding: Partial<CreateHolding>) {
    return this.request<Holding>(`/api/v1/portfolios/${portfolioId}/holdings/${holdingId}`, {
      method: 'PUT',
      body: JSON.stringify(holding),
    });
  }

  async deleteHolding(portfolioId: number, holdingId: number) {
    return this.request<void>(`/api/v1/portfolios/${portfolioId}/holdings/${holdingId}`, {
      method: 'DELETE',
    });
  }

  async addTransaction(portfolioId: number, transaction: CreateTransaction) {
    return this.request<Transaction>(`/api/v1/portfolios/${portfolioId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async getTransactions(portfolioId: number) {
    return this.request<Transaction[]>(`/api/v1/portfolios/${portfolioId}/transactions`);
  }

  // Alerts API
  async getAlerts() {
    return this.request<Alert[]>('/api/v1/alerts');
  }

  async createAlert(alert: CreateAlert) {
    return this.request<Alert>('/api/v1/alerts', {
      method: 'POST',
      body: JSON.stringify(alert),
    });
  }

  async updateAlert(id: number, alert: Partial<CreateAlert>) {
    return this.request<Alert>(`/api/v1/alerts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(alert),
    });
  }

  async deleteAlert(id: number) {
    return this.request<void>(`/api/v1/alerts/${id}`, {
      method: 'DELETE',
    });
  }

  // AI Analysis API
  async getAIAnalysis(symbol: string, analysisType: string = 'comprehensive', additionalContext?: string) {
    return this.request<AIAnalysisResponse>('/api/v1/ai/analyze', {
      method: 'POST',
      body: JSON.stringify({
        symbol,
        analysis_type: analysisType,
        additional_context: additionalContext || null
      }),
    });
  }

  async getPortfolioAIAnalysis(portfolioId: number) {
    return this.request<AIAnalysisResponse>(`/api/v1/ai/portfolio/${portfolioId}/analyze`);
  }

  async getAIAnalysisHistory(symbol?: string, limit: number = 50) {
    const params = new URLSearchParams();
    if (symbol) params.append('symbol', symbol);
    params.append('limit', limit.toString());
    return this.request<AIAnalysisHistoryItem[]>(`/api/v1/ai/history?${params.toString()}`);
  }

  async getAIAnalysisById(analysisId: number) {
    return this.request<AIAnalysisDetail>(`/api/v1/ai/history/${analysisId}`);
  }

  async getLatestAIAnalysis(symbol: string) {
    return this.request<AIAnalysisDetail>(`/api/v1/ai/latest/${symbol}`);
  }

  // Subscription & Payment API
  async getSubscriptionStatus() {
    return this.request<SubscriptionStatus>('/api/v1/payments/subscription');
  }

  async getEntitlements() {
    return this.request<EntitlementsResponse>('/api/v1/me/entitlements');
  }

  async createBankerCheckout() {
    return this.request<{ checkout_url: string }>('/api/v1/billing/banker/checkout', { method: 'POST' });
  }

  async getBankerBillingStatus() {
    return this.request<BankerBillingStatus>('/api/v1/billing/banker/status');
  }

  async manageBankerSubscription() {
    return this.request<{ checkout_url: string }>('/api/v1/billing/banker/manage', { method: 'POST' });
  }

  async payNowBanker() {
    return this.request<{ url: string }>('/api/v1/billing/banker/pay-now', { method: 'POST' });
  }

  async cancelBankerSubscription() {
    return this.request<{ cancelled: boolean }>('/api/v1/billing/banker/cancel', { method: 'POST' });
  }

  async getAvailablePlans(currency: string = 'ZAR') {
    return this.request<AvailablePlans>(`/api/v1/payments/plans?currency=${currency}`);
  }

  async createCheckoutSession(data: CreateCheckoutSession) {
    return this.request<CheckoutSessionResponse>('/api/v1/payments/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async cancelSubscription(reason?: string, feedback?: string) {
    return this.request<{ message: string }>('/api/v1/payments/cancel', {
      method: 'POST',
      body: JSON.stringify({ reason, feedback }),
    });
  }

  async getSubscriptionHistory() {
    return this.request<SubscriptionHistory[]>('/api/v1/payments/history/subscriptions');
  }

  async getPaymentHistory() {
    return this.request<PaymentHistory[]>('/api/v1/payments/history/payments');
  }

  async getAIQuota() {
    return this.request<AIQuota>('/api/v1/payments/ai-quota');
  }

  async verifyCheckoutSession(sessionId: string) {
    return this.request<{ success: boolean; message: string }>('/api/v1/payments/verify-session', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  // Email & Auth API
  async verifyEmail(token: string) {
    return this.request<{ message: string; email: string }>('/api/v1/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerificationEmail(email: string) {
    return this.request<{ message: string }>('/api/v1/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string }>('/api/v1/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.request<{ message: string }>('/api/v1/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  async getVerificationStatus() {
    return this.request<VerificationStatus>('/api/v1/auth/verification-status');
  }

  // Signals API
  async getSignal(symbol: string) {
    return this.request<SignalResponse>(`/api/v1/signals/${symbol}`);
  }

  async getBulkSignals(symbols: string[]) {
    return this.request<BulkSignalResponse>('/api/v1/signals/bulk', {
      method: 'POST',
      body: JSON.stringify(symbols),
    });
  }

  async getJSEScreenerSignals(filterType?: string, minConfidence: number = 0, limit: number = 100) {
    const params = new URLSearchParams();
    if (filterType) params.append('filter_type', filterType);
    if (minConfidence > 0) params.append('min_confidence', minConfidence.toString());
    params.append('limit', limit.toString());
    return this.request<ScreenerResponse>(`/api/v1/signals/screener/jse?${params}`);
  }

  async getUSScreenerSignals(filterType?: string, minConfidence: number = 0, limit: number = 100) {
    const params = new URLSearchParams();
    if (filterType) params.append('filter_type', filterType);
    if (minConfidence > 0) params.append('min_confidence', minConfidence.toString());
    params.append('limit', limit.toString());
    return this.request<ScreenerResponse>(`/api/v1/signals/screener/us?${params}`);
  }

  async postStrategyMatch(market: string) {
    return this.request<ScreenerResponse>('/api/v1/signals/screener/strategy-match', {
      method: 'POST',
      body: JSON.stringify({ market }),
    });
  }

  async getCustomScreenerSignals(symbols: string[], filterType?: string, minConfidence: number = 0) {
    const params = new URLSearchParams();
    if (filterType) params.append('filter_type', filterType);
    if (minConfidence > 0) params.append('min_confidence', minConfidence.toString());
    return this.request<ScreenerResponse>(`/api/v1/signals/screener/custom?${params}`, {
      method: 'POST',
      body: JSON.stringify(symbols),  // Send as plain array to match OpenAPI spec
    });
  }

  // Notifications API
  async getNotifications(options: NotificationQueryOptions = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    if (options.unread_only) params.append('unread_only', 'true');
    if (options.type) params.append('type', options.type);
    return this.request<NotificationListResponse>(`/api/v1/notifications?${params}`);
  }

  async getNotificationSummary() {
    return this.request<NotificationSummary>('/api/v1/notifications/summary');
  }

  async getUnreadCount() {
    return this.request<{ unread_count: number }>('/api/v1/notifications/unread-count');
  }

  // The backend has no bulk POST /mark-read route (only PATCH /{id}/read and
  // POST /mark-all-read), so mark each id individually.
  async markNotificationsRead(notificationIds: number[]) {
    await Promise.all(
      notificationIds.map((id) =>
        this.request<unknown>(`/api/v1/notifications/${id}/read`, { method: 'PATCH' })
      )
    );
    return { marked_read: notificationIds.length };
  }

  async markAllNotificationsRead() {
    return this.request<{ marked_read: number }>('/api/v1/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  async deleteNotification(id: number) {
    return this.request<{ deleted: boolean }>(`/api/v1/notifications/${id}`, {
      method: 'DELETE',
    });
  }

  // AI Activity Feed API
  async getActivityFeed(limit = 15, hours = 8, minConfidence = 80) {
    return this.request<FeedEvent[]>(
      `/api/v1/activity/feed?limit=${limit}&hours=${hours}&min_confidence=${minConfidence}`
    );
  }

  // Profile API
  async updateProfile(data: {
    name?: string;
    current_password?: string;
    new_password?: string;
    email?: string;
    fais_cat_ii_compliant?: boolean;
    fais_cat_ii_compliant_since?: string | null;
    fais_cat_ii_compliance_valid_until?: string | null;
    fais_cat_ii_certification_institution?: string | null;
    fais_cat_ii_certification_number?: string | null;
  }) {
    return this.request<UserProfile>('/api/v1/auth/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async confirmEmailChange(token: string) {
    return this.request<{ message: string }>(`/api/v1/auth/confirm-email-change?token=${encodeURIComponent(token)}`);
  }

  async cancelEmailChange() {
    return this.request<{ id: number; email: string }>('/api/v1/auth/me/email-change-pending', {
      method: 'DELETE',
    });
  }

  // User Preferences API
  async getPreferences() {
    return this.request<UserPreferences>('/api/v1/preferences');
  }

  async updatePreferences(preferences: UserPreferencesUpdate) {
    return this.request<UserPreferences>('/api/v1/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Promo Code API
  async validatePromoCode(code: string, plan?: string, amount?: number) {
    return this.request<PromoCodeValidationResponse>('/api/v1/promo/validate', {
      method: 'POST',
      body: JSON.stringify({ code, plan, amount }),
    });
  }

  // Account Deletion API
  async getDeletionStatus() {
    return this.request<DeletionStatusResponse>('/api/v1/account/deletion-status');
  }

  async requestDeletion(password: string, reason?: string) {
    return this.request<DeletionRequestResponse>('/api/v1/account/request-deletion', {
      method: 'POST',
      body: JSON.stringify({ password, reason }),
    });
  }

  async cancelDeletion(password: string) {
    return this.request<{ message: string }>('/api/v1/account/cancel-deletion', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async getStrategyBreakdown(symbol: string, options?: { force?: boolean }) {
    const params = options?.force ? '?force=true' : '';
    return this.request<EnrichedSignal>(
      `/api/v1/signals/${encodeURIComponent(symbol)}/strategy-breakdown${params}`
    );
  }

  async getStrategyHistory(market?: string, limit: number = 50) {
    const params = new URLSearchParams();
    if (market) params.set('market', market);
    if (limit !== 50) params.set('limit', String(limit));
    const qs = params.toString();
    return this.request<StrategyHistorySummary[]>(
      `/api/v1/signals/strategy-history${qs ? `?${qs}` : ''}`
    );
  }

  async getStrategyHistoryById(id: number) {
    return this.request<StrategyHistoryDetail>(
      `/api/v1/signals/strategy-history/${id}`
    );
  }
}

// Types
export interface UserProfile {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  subscription_tier: string;
  subscription_expires_at: string | null;
  fais_cat_ii_compliant?: boolean;
  fais_cat_ii_compliant_since?: string | null;
  fais_cat_ii_compliance_valid_until?: string | null;
  fais_cat_ii_certification_institution?: string | null;
  fais_cat_ii_certification_number?: string | null;
  created_at?: string | null;
  disclaimer_acknowledged?: boolean;
  org_id?: number | null;
}

export interface Watchlist {
  id: number;
  name: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  items: WatchlistItem[];
}

export interface WatchlistItem {
  id: number;
  watchlist_id: number;
  symbol: string;
  name: string;
  exchange: string;
  added_at: string;
}

export interface Portfolio {
  id: number;
  name: string;
  description?: string;
  user_id: number;
  created_at: string;
  updated_at: string;
  holdings: Holding[];
}

export interface Holding {
  id: number;
  portfolio_id: number;
  symbol: string;
  name: string;
  exchange: string;
  quantity: number;
  average_cost: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface CreateHolding {
  symbol: string;
  name: string;
  exchange: string;
  quantity: number;
  average_cost: number;
  currency: string;
}

export type TransactionType = 'buy' | 'sell';

export interface Transaction {
  id: number;
  portfolio_id: number;
  symbol: string;
  transaction_type: TransactionType;
  quantity: number;
  price: number;
  fees: number;
  notes?: string;
  transaction_date: string;
  created_at: string;
}

export interface CreateTransaction {
  symbol: string;
  transaction_type: TransactionType;
  quantity: number;
  price: number;
  fees?: number;
  notes?: string;
  transaction_date?: string;
}

export type AlertCondition = 'above' | 'below' | 'crosses_above' | 'crosses_below';
export type AlertType = 'price' | 'indicator';
export type IndicatorType = 'rsi' | 'macd' | 'sma_cross' | 'price_vs_sma' | 'momentum';

export interface Alert {
  id: number;
  user_id: number;
  symbol: string;
  name: string;
  condition: AlertCondition;
  target_price: number;
  is_active: boolean;
  is_triggered: boolean;
  triggered_at?: string;
  triggered_price?: number;
  created_at: string;
  // Technical indicator fields
  alert_type: AlertType;
  indicator_type?: IndicatorType;
  indicator_target?: number;
  triggered_indicator_value?: number;
}

export interface CreateAlert {
  symbol: string;
  name: string;
  condition: AlertCondition;
  target_price: number;
  // Technical indicator fields
  alert_type?: AlertType;
  indicator_type?: IndicatorType;
  indicator_target?: number;
}

export interface AIAnalysisResponse {
  symbol: string;
  analysis: string;
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  confidence: number;
  key_points: string[];
  risks: string[];
  generated_at: string;
  provider?: string;
  model_used?: string;
}

export interface AIAnalysisHistoryItem {
  id: number;
  symbol: string;
  analysis_type: string;
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  confidence: number;
  summary: string;
  created_at: string;
}

export interface AIAnalysisDetail {
  id: number;
  symbol: string;
  analysis_type: string;
  recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
  confidence: number;
  summary: string;
  analysis: string;
  key_points: string[];
  risks: string[];
  opportunities: string[];
  technical_outlook?: string;
  fundamental_outlook?: string;
  additional_context?: string;
  created_at: string;
  provider?: string;
  model_used?: string;
}

export interface StrategyHistorySummary {
  id: number;
  market: string | null;
  strategy_names: string[];
  signal_count: number;
  computed_at: string;
  expires_at: string;
}

export interface StrategyHistoryDetail {
  id: number;
  market: string | null;
  strategy_names: string[];
  computed_at: string;
  expires_at: string;
  signals: any[];
}

// Subscription Types
export type SubscriptionTier = 'free' | 'pro' | 'premium';
export type PaymentProvider = 'stripe' | 'payfast';

export interface SubscriptionStatus {
  tier: SubscriptionTier;
  plan: string | null;
  is_active: boolean;
  expires_at: string | null;
  will_renew: boolean;
  provider: PaymentProvider | null;
  ai_requests_used: number;
  ai_requests_limit: number;
}

export interface InvestmentBankingEntitlement {
  active: boolean;
  state: 'none' | 'trialing' | 'active' | 'renewal_due' | 'overdue_disabled' | 'cancelled' | 'grandfathered' | string;
  source: 'none' | 'paid' | 'grandfathered' | string;
  trial_ends_at: string | null;
  due_at: string | null;
  days_until_due?: number | null;
  reminder_card?: 'none' | 'due_soon' | 'overdue';
}

export interface EntitlementsResponse {
  investment_banking_gate: boolean;
  investment_banking: InvestmentBankingEntitlement;
  [key: string]: unknown;
}

export interface BankerBillingStatus {
  investment_banking: InvestmentBankingEntitlement;
  display_price: string;
  trial_days: number;
}

export interface PlanInfo {
  tier: SubscriptionTier;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  ai_quota: number;
}

export interface AvailablePlans {
  plans: PlanInfo[];
}

export interface CreateCheckoutSession {
  tier: SubscriptionTier;
  provider: PaymentProvider;
}

export interface CheckoutSessionResponse {
  session_id: string;
  checkout_url: string;
  provider: PaymentProvider;
}

export interface SubscriptionHistory {
  id: number;
  plan: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  starts_at: string;
  expires_at: string;
  cancelled_at: string | null;
  created_at: string;
}

export interface PaymentHistory {
  id: number;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  payment_method: string | null;
  description: string | null;
  created_at: string;
}

export interface AIQuota {
  tier: string;
  quota_limit: number;
  current_usage: number;
  remaining: number;
  percentage_used: number;
  is_unlimited: boolean;
  reset_date: string | null;
}

export interface VerificationStatus {
  email: string;
  is_verified: boolean;
}

import type { StrategySignalFields, EnrichedSignal } from '../types/signals';

// Signal Types
// Canonical backend payloads may include legacy HOLD or normalized NEUTRAL values.
export type SignalType = 'buy' | 'hold' | 'neutral' | 'sell' | 'strong_buy' | 'strong_sell';
export type SignalStrength = 'strong' | 'moderate' | 'weak';

export interface TechnicalIndicator {
  name: string;
  value: number;
  signal: SignalType;
  description: string;
}

export interface SignalResponse extends StrategySignalFields {
  symbol: string;
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  price: number;
  indicators: TechnicalIndicator[];
  reasoning: string;
  generated_at: string;
}

export interface BulkSignalResponse {
  signals: SignalResponse[];
}

export interface ScreenerSignal extends StrategySignalFields {
  symbol: string;
  name?: string;
  signal: SignalType;
  strength: SignalStrength;
  confidence: number;
  timing?: 'now' | 'next_candle' | 'watch';
  price: number;
  change_percent: number;
  previousClose?: number;
  rsi?: number;
  day_drop_override?: boolean;
}

export interface ScreenerResponse {
  signals: ScreenerSignal[];
  total: number;
  filter_applied?: string;
}

// Notification Types
export type NotificationType = 'alert' | 'signal' | 'portfolio' | 'system' | 'payment';
export type NotificationPriority = 'low' | 'medium' | 'high';

export interface Notification {
  id: number;
  notification_type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  link?: string | null;
  is_read: boolean;
  read_at?: string;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  limit: number;
  offset: number;
}

export interface NotificationSummary {
  total: number;
  unread: number;
  type_counts: Record<string, number>;
}

export interface NotificationQueryOptions {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
  type?: NotificationType;
}

export interface FeedEvent {
  id: string;
  event_type: string;
  title: string;
  description: string;
  symbol?: string | null;
  direction?: string | null;
  confidence?: number | null;
  priority?: string | null;
  link?: string | null;
  timestamp: string;
}

// User Preferences Types
export interface NotificationPreferencesAPI {
  price_alerts: boolean;
  portfolio_updates: boolean;
  market_news: boolean;
  email_digest: boolean;
  email_on_alert_trigger: boolean;
}

export interface DisplayPreferencesAPI {
  currency: string;
  theme: string;
  compact_mode: boolean;
  accent_color: string;
  font_size: string;
  chart_color_scheme: string;
  sidebar_collapsed: boolean;
  animation_intensity: string;
  show_price_ticker: boolean;
}

export interface UserPreferences {
  notifications: NotificationPreferencesAPI;
  display: DisplayPreferencesAPI;
}

export interface UserPreferencesUpdate {
  notifications?: Partial<NotificationPreferencesAPI>;
  display?: Partial<DisplayPreferencesAPI>;
}

// Promo Code Types
export type DiscountType = 'percentage' | 'fixed';

export interface PromoCodeValidationResponse {
  valid: boolean;
  code: string;
  message: string;
  discount_type?: DiscountType;
  discount_value?: number;
  discount_amount?: number;
  final_amount?: number;
  applicable_plans?: string[];
}

// Account Deletion Types
export interface DeletionStatusResponse {
  deletion_requested: boolean;
  deletion_requested_at?: string;
  deletion_scheduled_at?: string;
  deletion_reason?: string;
  days_remaining?: number;
}

export interface DeletionRequestResponse {
  message: string;
  deletion_scheduled_at: string;
  grace_period_days: number;
  can_cancel_until: string;
}

export const api = new ApiService();
export default api;
